import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAdvisedCriticalItemsHistoryCount } from "components/AdvisedCriticalHistory/components/AdvisedCriticalHistoryTable/hooks";
import { getCustomerActionsCount, getNotesCount, getSnoozedItemsCount } from "modules/AppointmentDetails/components";
import { useActivityLogsCount } from "modules/AppointmentDetails/components/ActivityLog/utils";

import type { SectionMenu } from "components";
import type { Appointment, CarCheck } from "models";

export type AppointmentDetailsScrollToSectionMenus = {
  details: SectionMenu;
  customerActions: SectionMenu;
  snoozedItems: SectionMenu;
  advisedCriticalHistory: SectionMenu;
  notes: SectionMenu;
  interventions: SectionMenu;
  tyreReplacements: SectionMenu;
  checks: SectionMenu;
  activityLogs: SectionMenu;
};

export const useScrollToSectionMenus = (isTyreStatusVisible: boolean, appointment?: Appointment, checks?: CarCheck[]) => {
  const { t, ready } = useTranslation();

  const advisedCriticalHistoryCount = useAdvisedCriticalItemsHistoryCount({ appointment_id: appointment?.id, car_id: appointment?.car_id });
  const activityLogsCount = useActivityLogsCount(appointment);
  const tyreReplacementsCount = appointment?.car?.tyres?.reduce((sum, tyre) => sum + (tyre.replacements?.length ?? 0), 0);

  return useMemo(
    (): AppointmentDetailsScrollToSectionMenus => ({
      details: { id: "details", icon: "info circle", title: t("v8_car_customer_details", "Car & Customer details"), showEmpty: true },
      customerActions: {
        id: "customerActions",
        icon: "calendar check regular",
        title: t("v8_customer_actions", "Customer Actions"),
        count: getCustomerActionsCount(appointment)
      },
      snoozedItems: {
        id: "snoozedItems",
        icon: "clock",
        title: t("v8_snoozed_items", "Snoozed Items"),
        count: getSnoozedItemsCount(appointment?.interventions, checks)
      },
      advisedCriticalHistory: {
        id: "advisedCriticalHistory",
        icon: "clock rotate left",
        title: t("v8_advised_critical_items_history", "Advised and critical items history"),
        count: advisedCriticalHistoryCount
      },
      notes: { id: "notes", icon: "note sticky", title: t("v8_notes", "Notes"), count: getNotesCount(appointment), showEmpty: true },
      interventions: {
        id: "interventions",
        icon: "screwdriver wrench",
        title: t("v8_interventions", "Interventions"),
        count: appointment?.interventions?.length ?? 0,
        showEmpty: true
      },
      tyreReplacements: {
        id: "tyreReplacements",
        icon: "tire regular",
        title: t("v8_tyre_replacements", "Tyre Replacements"),
        count: isTyreStatusVisible ? tyreReplacementsCount : 0
      },
      checks: { id: "checks", icon: "list", title: "", count: checks?.length },
      activityLogs: { id: "activityLogs", icon: "user clock", title: t("v8_activity_log", "Activity Log"), count: activityLogsCount }
    }),
    [ready, appointment, checks, advisedCriticalHistoryCount, activityLogsCount, tyreReplacementsCount, isTyreStatusVisible]
  );
};
